import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { Phase } from '@/types/user';
import { PhasePill } from '@/features/dashboard/PhaseTrackerCards';
import Phase1 from '@/features/dashboard/Phase1';
import Phase2 from '@/features/dashboard/Phase2';
import Phase3 from '@/features/dashboard/Phase3';
import Phase4 from '@/features/dashboard/Phase4';
import Phase5 from '@/features/dashboard/Phase5';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import TodaysFocus from '@/features/dashboard/TodaysFocus';
// WEEKLY ONE SIDEBAR: hidden temporarily. To re-enable:
// 1. Uncomment the import below
// 2. Replace the single-column layout with the 2-column grid (see git history: commit ab2acb9)
// import WeeklyOneCard from '@/features/weekly-one/WeeklyOneCard';

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const { isMember, isAdmin, loading } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isMember) navigate('/calculators', { replace: true });
  }, [loading, isMember, navigate]);

  // Belt-and-suspenders: if a migrated member somehow lands on /dashboard
  // before completing the placement quiz, send them to /welcome.
  useEffect(() => {
    if (user?.migrationStatus === 'pending_first_login') {
      navigate('/welcome', { replace: true });
    }
  }, [user?.migrationStatus, navigate]);

  const shouldReduceMotion = useReducedMotion();

  if (loading) return null;
  if (!isMember) return null;
  if (!user) return null;

  const phaseComponents = { 1: Phase1, 2: Phase2, 3: Phase3, 4: Phase4, 5: Phase5 };
  const PhaseComponent = phaseComponents[user.phase];

  const handlePhaseClick = (phase: Phase) => {
    updateUser({ phase });
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          className="flex items-start justify-between gap-3 mb-0 sm:mb-1"
        >
          <h1 className="font-serif text-[28px] sm:text-[36px] font-medium text-foreground leading-tight">
            Welcome back,{' '}
            <motion.span
              initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, delay: 0.12, ease: [0.23, 1, 0.32, 1] }}
              className="text-gold italic font-semibold inline-block"
            >
              {user.name}
            </motion.span>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: 0.07, ease: [0.23, 1, 0.32, 1] }}
            className="hidden sm:flex sm:flex-col sm:items-end shrink-0 pt-1 gap-1.5"
          >
            <PhasePill currentPhase={user.phase} onPhaseClick={handlePhaseClick} isAdmin={isAdmin} />
            <Link to="/roadmap" className="text-[11px] text-muted-foreground hover:text-gold transition-colors">
              View your full First Deal journey
            </Link>
          </motion.div>
        </motion.div>
        <div className="sm:hidden mt-4">
          <div className="overflow-x-auto">
            <PhasePill currentPhase={user.phase} onPhaseClick={handlePhaseClick} isAdmin={isAdmin} />
          </div>
          <Link to="/roadmap" className="block mt-1.5 text-[11px] text-muted-foreground hover:text-gold transition-colors">
            View your full First Deal journey
          </Link>
        </div>
      </div>

      {/* Today's Focus -- hidden on Phase 1 (Sign Declaration bubble redundant),
          Phase 2 (Fast Track call bubble redundant), and Phase 4 (Momentum Rail
          already surfaces the Weekly One CTA inline). */}
      {user.phase !== 1 && user.phase !== 2 && user.phase !== 4 && user.phase !== 5 && <TodaysFocus />}

      <AnimatePresence mode="wait">
        <motion.div
          key={user.phase}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
        >
          {PhaseComponent && <PhaseComponent />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
